import {useResourceManagement} from "../../hooks/resourceHooks";
import {getIrritileById, getIrritileFields, getIrritileOrders} from "../../services/irritilesService";
import {Grid, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from "@mui/material";
import {useEffect, useState} from "react";
import {createFeatureCollection} from "../../utils/mapUtils";
import {FeatureCollection, MultiPolygon, Polygon, GeoJsonProperties} from "geojson";
import {MapDataType} from "../../types/mapDataType";
import {IrritilesMap} from "../map/IrritilesMap";
import {useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {KeyValuePair} from "../base/KeyValuePair";
import {Irritile} from "../../types/domain/irritiles";
import {Order} from "../../types/domain/order";

export const AdminIrriTileDetailsView: React.FC = () => {
    const {t} = useTranslation();
    const [geojson, setGeojson] = useState<FeatureCollection<MultiPolygon | Polygon, GeoJsonProperties> | null>(null);
    const [irritile, setIrritile] = useState<Irritile>();
    const [orders, setOrders] = useState<Order[]>();
    const irritilesManagement = useResourceManagement({
        getIrritileById,
        getIrritileFields,
        getIrritileOrders,
    });
    const irritileId = useParams().irritileId;

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        void Promise.all([
            irritilesManagement.getIrritileById(irritileId!),
            irritilesManagement.getIrritileFields(irritileId!),
            irritilesManagement.getIrritileOrders(irritileId!),
        ]).then(([irritile, fields, orders]) => {
            setIrritile(irritile);
            setOrders(orders);
            setGeojson(
                createFeatureCollection<{label?: string}>([
                    {id: irritile.id, name: "irritile", geometry: irritile.extent},
                    ...fields.map((field) => ({
                        id: field.id,
                        name: "fields",
                        geometry: field.geometry,
                        label: field.name,
                    })),
                ]),
            );
        });
    }, [irritileId]);

    return (
        <Stack direction="column" sx={{height: "100%"}} gap="0.5rem">
            <IrritilesMap
                geojson={geojson}
                showRealign={true}
                showSlider={true}
                dataType={MapDataType.IRRITILE}
                enablePopup={true}
            />
            <Stack sx={{height: "30rem", padding: "1rem", overflow: "auto"}}>
                <Grid container>
                    <Grid item xs={6}>
                        <Stack direction="column" gap="0.5rem">
                            <Typography fontSize={16} textTransform="uppercase" sx={{mb: "1rem", ml: "0.75rem"}}>
                                {t("admin.irritile_details_view.irritile")}
                            </Typography>
                            <Stack padding="1rem">
                                <Grid item xs={12} paddingBottom="0.5rem">
                                    <KeyValuePair header={t("admin.irritile_details_view.id")}>
                                        <Typography>{irritile?.id}</Typography>
                                    </KeyValuePair>
                                </Grid>
                                <Grid item xs={12} paddingBottom="0.5rem">
                                    <KeyValuePair header={t("admin.irritile_details_view.state")}>
                                        <Typography>
                                            {irritile?.isActive
                                                ? t("admin.irritile_details_view.active")
                                                : t("admin.irritile_details_view.inactive")}
                                        </Typography>
                                    </KeyValuePair>
                                </Grid>
                                <Grid item xs={12} paddingBottom="0.5rem">
                                    <KeyValuePair header={t("admin.irritile_details_view.utmzone")}>
                                        <Typography>{irritile?.utmZone}</Typography>
                                    </KeyValuePair>
                                </Grid>
                                <Grid item xs={12} paddingBottom="0.5rem">
                                    <KeyValuePair header={t("admin.irritile_details_view.weather")}>
                                        <Typography>{irritile?.weatherSources}</Typography>
                                    </KeyValuePair>
                                </Grid>
                            </Stack>
                        </Stack>
                    </Grid>
                    <Grid item xs={6}>
                        <Stack direction="column" gap="0.5rem">
                            <Typography fontSize={16} textTransform="uppercase" sx={{mb: "1rem", ml: "0.75rem"}}>
                                {t("admin.irritile_details_view.orders")}
                            </Typography>
                            <Stack>
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{"th, td": {borderColor: "rgba(0, 0, 0, 0.12);"}}}>
                                                <TableCell>
                                                    <Typography
                                                        textTransform="uppercase"
                                                        sx={{color: "#737990"}}
                                                        fontSize={14}
                                                    >
                                                        {t("admin.irritile_details_view.order_id")}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        textTransform="uppercase"
                                                        sx={{color: "#737990"}}
                                                        fontSize={14}
                                                    >
                                                        {t("admin.irritile_details_view.state")}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {orders?.map((order) => (
                                                <TableRow
                                                    key={order.id}
                                                    sx={{
                                                        "&:last-child td, &:last-child th": {border: 0},
                                                        "th, td": {borderColor: "rgba(0, 0, 0, 0.12);"},
                                                    }}
                                                >
                                                    <TableCell component="th" scope="row">
                                                        {order.id?.substring(0, 8)}
                                                    </TableCell>
                                                    <TableCell>{order.state}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Stack>
                        </Stack>
                    </Grid>
                </Grid>
            </Stack>
        </Stack>
    );
};
